import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import type { QuadroToastState } from "@/components/kanban/QuadroToast";
import { api } from "@/lib/api";
import { mensagemDaFalha } from "@/lib/mensagem-de-falha";
import { quandoVolta, type AdiarAte } from "@/lib/notificacoes/caixa";
import type { NotificationItem } from "@/lib/types";

/** Quanto o aviso com "Desfazer" fica na tela (o da prévia). */
const TEMPO_DO_AVISO = 7_000;

/** `silenciosa`: o "Desfazer", que não abre outro aviso. */
type Acao = { id: string; silenciosa?: boolean } & (
  | { tipo: "lida" | "naoLida" | "reativar" | "arquivar" | "restaurar" }
  | { tipo: "adiar"; ate: AdiarAte }
);

/**
 * TUT-586b — as ações do aviso, as MESMAS no painel do sino e na página:
 * lida/não lida (586a), adiar e reativar, arquivar e restaurar. Adiar e
 * arquivar tiram o aviso da lista, então dizem o que fizeram num aviso com
 * "Desfazer" (o `QuadroToast` da casa). `aoMudar` deixa a página atualizar o
 * aviso aberto no detalhe sem esperar a lista.
 */
export function useAcoesDoAviso(aoMudar?: (id: string, mudanca: Partial<NotificationItem>) => void) {
  const queryClient = useQueryClient();
  const [falha, setFalha] = useState<string | null>(null);
  const [toast, setToast] = useState<QuadroToastState | null>(null);
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (relogio.current) clearTimeout(relogio.current);
  }, []);

  function avisar(message: string, onUndo: (() => void) | null) {
    if (relogio.current) clearTimeout(relogio.current);
    setToast({ id: Date.now(), message, tone: "success", onUndo });
    relogio.current = setTimeout(() => setToast(null), TEMPO_DO_AVISO);
  }

  const mutacao = useMutation({
    mutationFn: async (acao: Acao): Promise<Partial<NotificationItem>> => {
      switch (acao.tipo) {
        case "lida":
          await api.markNotificationRead(acao.id);
          return { read: true };
        case "naoLida":
          await api.markNotificationUnread(acao.id);
          return { read: false };
        case "adiar":
          return { snoozedUntil: (await api.snoozeNotification(acao.id, acao.ate)).snoozedUntil };
        case "reativar":
          await api.unsnoozeNotification(acao.id);
          return { snoozedUntil: null };
        case "arquivar":
          await api.archiveNotification(acao.id);
          return { archivedAt: new Date().toISOString(), snoozedUntil: null };
        case "restaurar":
          await api.unarchiveNotification(acao.id);
          return { archivedAt: null };
      }
    },
    onSuccess: async (mudanca, acao) => {
      setFalha(null);
      aoMudar?.(acao.id, mudanca);
      if (acao.silenciosa) setToast(null);
      else if (acao.tipo === "adiar" && mudanca.snoozedUntil) {
        avisar(`Adiado: volta ${quandoVolta(mudanca.snoozedUntil)}`, () => fazer({ tipo: "reativar", id: acao.id, silenciosa: true }));
      } else if (acao.tipo === "arquivar") {
        avisar("Arquivado", () => fazer({ tipo: "restaurar", id: acao.id, silenciosa: true }));
      } else if (acao.tipo === "restaurar") {
        avisar("Restaurado", () => fazer({ tipo: "arquivar", id: acao.id, silenciosa: true }));
      } else if (acao.tipo === "reativar") {
        avisar("Voltou para a lista", null);
      }
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (erro: unknown) => setFalha(mensagemDaFalha(erro, "Não foi possível atualizar as notificações.")),
  });

  function fazer(acao: Acao) {
    mutacao.mutate(acao);
  }

  return {
    falha,
    setFalha,
    toast,
    fecharToast: () => setToast(null),
    ocupado: mutacao.isPending,
    alternarLida: (item: NotificationItem) => fazer({ tipo: item.read ? "naoLida" : "lida", id: item.id }),
    marcarLida: (item: NotificationItem) => {
      if (!item.read) fazer({ tipo: "lida", id: item.id });
    },
    adiar: (item: NotificationItem, ate: AdiarAte) => fazer({ tipo: "adiar", id: item.id, ate }),
    reativar: (item: NotificationItem) => fazer({ tipo: "reativar", id: item.id }),
    arquivar: (item: NotificationItem) => fazer({ tipo: "arquivar", id: item.id }),
    restaurar: (item: NotificationItem) => fazer({ tipo: "restaurar", id: item.id }),
  };
}
